"use client"

import { useEffect, useLayoutEffect, useReducer, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { AnimatePresence, motion } from "framer-motion"
import { LabelEditorScene } from "./scenes/label-editor"
import { PostsScene } from "./scenes/posts"
import { PostDetailsScene } from "./scenes/post-details"
import { ActsScene } from "./scenes/acts"
import { ReportsScene } from "./scenes/reports"

const STAGE_W = 1280
const STAGE_H = 820

type SceneKey = "erid" | "label" | "posts" | "post" | "acts" | "reports"

const SCENES: {
  key: SceneKey
  label: string
  durationMs: number
  render: (active: boolean) => React.ReactNode
}[] = [
  // { key: "erid", label: "Получение ERID", durationMs: 14000, render: (a) => <EridScene active={a} /> },
  { key: "label", label: "Редактор маркировки", durationMs: 10000, render: (a) => <LabelEditorScene active={a} /> },
  { key: "posts", label: "История публикаций", durationMs: 8500, render: (a) => <PostsScene active={a} /> },
  { key: "post", label: "Детали публикации", durationMs: 10000, render: (a) => <PostDetailsScene active={a} /> },
  { key: "acts", label: "Создание акта", durationMs: 15000, render: (a) => <ActsScene active={a} /> },
  // «reports» вынесен в отдельную секцию §03 (см. DashboardPreviewSection)
]

// Экспорт всех сцен для HeroDemoStatic — он ищет и среди нерегулярных.
const ALL_SCENES: { key: SceneKey; label: string; render: (active: boolean) => React.ReactNode }[] = [
  ...SCENES,
  { key: "reports", label: "Отчётность", render: (a) => <ReportsScene active={a} /> },
]

export function HeroDemoCarousel() {
  const [idx, dispatch] = useReducer(
    (s: number, action: "next" | { type: "set"; i: number }) =>
      action === "next" ? (s + 1) % SCENES.length : action.i,
    0,
  )
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const frameRef = useRef<HTMLDivElement>(null)
  const [scale, setScale] = useState(0.5)
  const [fs, setFs] = useState(false)

  useLayoutEffect(() => {
    const el = frameRef.current
    if (!el) return
    const ro = new ResizeObserver(() => setScale(el.clientWidth / STAGE_W))
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => dispatch("next"), SCENES[idx].durationMs)
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [idx])

  useEffect(() => {
    if (!fs) return
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setFs(false) }
    document.addEventListener("keydown", onKey)
    document.body.style.overflow = "hidden"
    return () => {
      document.removeEventListener("keydown", onKey)
      document.body.style.overflow = ""
    }
  }, [fs])

  const active = SCENES[idx]

  return (
    <div className="relative">
      <div
        role="button"
        tabIndex={0}
        onClick={() => setFs(true)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault()
            setFs(true)
          }
        }}
        aria-label="Открыть в полном режиме"
        className="group block w-full cursor-pointer text-left"
      >
        <div
          ref={frameRef}
          className="relative overflow-hidden rounded-[8px] border border-[rgba(11,16,32,0.1)] bg-white"
          style={{
            aspectRatio: `${STAGE_W} / ${STAGE_H}`,
            boxShadow: "0 1px 0 rgba(0,0,0,0.04), 20px 24px 0 -12px rgba(234,88,12,0.15)",
          }}
        >
          <AnimatePresence mode="sync">
            {SCENES.map((s, i) =>
              i === idx ? (
                <motion.div
                  key={s.key}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                  className="absolute top-0 left-0"
                  style={{
                    width: STAGE_W,
                    height: STAGE_H,
                    transform: `scale(${scale})`,
                    transformOrigin: "top left",
                    pointerEvents: "none",
                  }}
                >
                  {s.render(!fs)}
                </motion.div>
              ) : null,
            )}
          </AnimatePresence>

          <span className="absolute right-3 top-3 z-10 flex h-8 w-8 items-center justify-center rounded-md border border-[rgba(11,16,32,0.12)] bg-white/90 text-[#0b1020] opacity-0 shadow-sm transition-opacity group-hover:opacity-100">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15 3 21 3 21 9" />
              <polyline points="9 21 3 21 3 15" />
              <line x1="21" y1="3" x2="14" y2="10" />
              <line x1="3" y1="21" x2="10" y2="14" />
            </svg>
          </span>
        </div>
      </div>

      <div className="mt-5 flex items-center justify-center gap-1.5 flex-wrap">
        {SCENES.map((s, i) => (
          <button
            key={s.key}
            onClick={() => dispatch({ type: "set", i })}
            className="group flex items-center gap-1.5"
            aria-label={`Показать сцену: ${s.label}`}
          >
            <span
              className={`block h-1 rounded-full transition-all duration-300 ${
                i === idx
                  ? "w-10 bg-[#ea580c]"
                  : "w-3 bg-[rgba(11,16,32,0.15)] group-hover:bg-[rgba(11,16,32,0.3)]"
              }`}
            />
          </button>
        ))}
      </div>
      <div className="mt-2 text-center font-mono text-[10px] uppercase tracking-[0.14em] text-[rgba(11,16,32,0.5)]">
        {active.label}
      </div>

      {fs && typeof document !== "undefined" && createPortal(
        <FullscreenCarousel idx={idx} dispatch={dispatch} onClose={() => setFs(false)} />,
        document.body,
      )}
    </div>
  )
}

function FullscreenCarousel({
  idx, dispatch, onClose,
}: {
  idx: number
  dispatch: (action: "next" | { type: "set"; i: number }) => void
  onClose: () => void
}) {
  const frameRef = useRef<HTMLDivElement>(null)
  const [scale, setScale] = useState(1)
  const [rotated, setRotated] = useState(false)
  useLayoutEffect(() => {
    const fit = () => {
      const isMobile = window.innerWidth < 768
      const padX = isMobile ? 16 : 80
      const padY = isMobile ? 120 : 160
      const vw = window.innerWidth - padX
      const vh = window.innerHeight - padY
      const normal = Math.min(vw / STAGE_W, vh / STAGE_H)
      const rotatedScale = Math.min(vw / STAGE_H, vh / STAGE_W)
      const shouldRotate = isMobile && window.innerHeight > window.innerWidth && rotatedScale > normal
      setRotated(shouldRotate)
      setScale(shouldRotate ? rotatedScale : normal)
    }
    fit()
    window.addEventListener("resize", fit)
    window.addEventListener("orientationchange", fit)
    return () => {
      window.removeEventListener("resize", fit)
      window.removeEventListener("orientationchange", fit)
    }
  }, [])
  const active = SCENES[idx]
  const boxW = (rotated ? STAGE_H : STAGE_W) * scale
  const boxH = (rotated ? STAGE_W : STAGE_H) * scale
  return (
    <div className="fixed inset-0 z-[200] flex flex-col items-center justify-center bg-[rgba(11,16,32,0.9)] px-2 py-4 sm:p-10 backdrop-blur-sm">
      <button
        onClick={onClose}
        aria-label="Закрыть"
        className="absolute right-3 top-3 sm:right-5 sm:top-5 flex h-10 w-10 items-center justify-center rounded-md bg-white/10 text-white hover:bg-white/20 z-10"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
      <div
        ref={frameRef}
        className="relative overflow-hidden rounded-lg bg-white shadow-2xl"
        style={{ width: boxW, height: boxH }}
      >
        <div
          style={{
            width: STAGE_W,
            height: STAGE_H,
            transform: rotated
              ? `translate(${STAGE_H * scale}px, 0) rotate(90deg) scale(${scale})`
              : `scale(${scale})`,
            transformOrigin: "top left",
          }}
        >
          {active.render(true)}
        </div>
      </div>
      <div className="mt-6 flex items-center justify-center gap-2">
        {SCENES.map((s, i) => (
          <button
            key={s.key}
            onClick={() => dispatch({ type: "set", i })}
            aria-label={s.label}
            className={`h-1.5 rounded-full transition-all ${
              i === idx ? "w-12 bg-[#ea580c]" : "w-4 bg-white/30 hover:bg-white/50"
            }`}
          />
        ))}
      </div>
      <div className="mt-3 font-mono text-[11px] uppercase tracking-[0.14em] text-white/70">
        {active.label}
      </div>
    </div>
  )
}

// Статичный превью конкретной сцены — для секции «03 · Интерфейс»
export function HeroDemoStatic({ scene }: { scene: SceneKey }) {
  const frameRef = useRef<HTMLDivElement>(null)
  const [scale, setScale] = useState(0.5)
  const [fs, setFs] = useState(false)
  useLayoutEffect(() => {
    const el = frameRef.current
    if (!el) return
    const ro = new ResizeObserver(() => setScale(el.clientWidth / STAGE_W))
    ro.observe(el)
    return () => ro.disconnect()
  }, [])
  useEffect(() => {
    if (!fs) return
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setFs(false) }
    document.addEventListener("keydown", onKey)
    document.body.style.overflow = "hidden"
    return () => {
      document.removeEventListener("keydown", onKey)
      document.body.style.overflow = ""
    }
  }, [fs])
  const found = ALL_SCENES.find((s) => s.key === scene) ?? ALL_SCENES[0]
  return (
    <>
      <div
        role="button"
        tabIndex={0}
        onClick={() => setFs(true)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault()
            setFs(true)
          }
        }}
        aria-label="Открыть в полном режиме"
        className="group block w-full cursor-pointer text-left"
      >
        <div
          ref={frameRef}
          className="relative overflow-hidden rounded-[8px] border border-[rgba(11,16,32,0.1)] bg-white"
          style={{
            aspectRatio: `${STAGE_W} / ${STAGE_H}`,
          }}
        >
          <div
            className="absolute top-0 left-0"
            style={{
              width: STAGE_W,
              height: STAGE_H,
              transform: `scale(${scale})`,
              transformOrigin: "top left",
              pointerEvents: "none",
            }}
          >
            {found.render(true)}
          </div>
          <span className="absolute right-3 top-3 z-10 flex h-8 w-8 items-center justify-center rounded-md border border-[rgba(11,16,32,0.12)] bg-white/90 text-[#0b1020] opacity-0 shadow-sm transition-opacity group-hover:opacity-100">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15 3 21 3 21 9" />
              <polyline points="9 21 3 21 3 15" />
              <line x1="21" y1="3" x2="14" y2="10" />
              <line x1="3" y1="21" x2="10" y2="14" />
            </svg>
          </span>
        </div>
      </div>
      {fs && typeof document !== "undefined" && createPortal(
        <FullscreenStatic render={found.render} label={found.label} onClose={() => setFs(false)} />,
        document.body,
      )}
    </>
  )
}

function FullscreenStatic({
  render, label, onClose,
}: {
  render: (active: boolean) => React.ReactNode
  label: string
  onClose: () => void
}) {
  const [scale, setScale] = useState(1)
  const [rotated, setRotated] = useState(false)
  useLayoutEffect(() => {
    const fit = () => {
      const isMobile = window.innerWidth < 768
      const padX = isMobile ? 16 : 80
      const padY = isMobile ? 100 : 140
      const vw = window.innerWidth - padX
      const vh = window.innerHeight - padY
      const normal = Math.min(vw / STAGE_W, vh / STAGE_H)
      const rotatedScale = Math.min(vw / STAGE_H, vh / STAGE_W)
      const shouldRotate = isMobile && window.innerHeight > window.innerWidth && rotatedScale > normal
      setRotated(shouldRotate)
      setScale(shouldRotate ? rotatedScale : normal)
    }
    fit()
    window.addEventListener("resize", fit)
    window.addEventListener("orientationchange", fit)
    return () => {
      window.removeEventListener("resize", fit)
      window.removeEventListener("orientationchange", fit)
    }
  }, [])
  const boxW = (rotated ? STAGE_H : STAGE_W) * scale
  const boxH = (rotated ? STAGE_W : STAGE_H) * scale
  return (
    <div className="fixed inset-0 z-[200] flex flex-col items-center justify-center bg-[rgba(11,16,32,0.9)] px-2 py-4 sm:p-10 backdrop-blur-sm">
      <button
        onClick={onClose}
        aria-label="Закрыть"
        className="absolute right-3 top-3 sm:right-5 sm:top-5 z-10 flex h-10 w-10 items-center justify-center rounded-md bg-white/10 text-white hover:bg-white/20"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
      <div
        className="relative overflow-hidden rounded-lg bg-white shadow-2xl"
        style={{ width: boxW, height: boxH }}
      >
        <div
          style={{
            width: STAGE_W,
            height: STAGE_H,
            transform: rotated
              ? `translate(${STAGE_H * scale}px, 0) rotate(90deg) scale(${scale})`
              : `scale(${scale})`,
            transformOrigin: "top left",
          }}
        >
          {render(true)}
        </div>
      </div>
      <div className="mt-3 font-mono text-[11px] uppercase tracking-[0.14em] text-white/70">
        {label}
      </div>
    </div>
  )
}
